import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { StatusPill } from '@/components/StatusPill';
import { ScheduleEditor } from '@/components/ScheduleEditor';
import { PickupToggle } from '@/components/PickupToggle';
import { KtInsightsPagePicker } from '@/components/KtInsightsPagePicker';
// The report's own filter panel, shared with the report pack editor. Extracted from this file
// rather than copied into that one — two filter pickers would drift the first time the estate
// added a filter type, and the drift would land as an export carrying different filters depending
// on which screen scheduled it.
import { KtInsightsFiltersEditor } from '@/components/KtInsightsFiltersEditor';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import type { PageEntitlement } from '@/components/KtInsightsPagePicker';
import type {
  KtInsightsDateRange,
  KtInsightsDelivery,
  KtInsightsExportOutcome,
  KtInsightsExportStatus,
  KtInsightsParameters,
  KtInsightsRecipient,
  KtInsightsRunNowResult,
  KtInsightsSubscription,
  PickupDeliveryDto,
  ScheduleDefinition
} from '@/types';
import { ALL_MONTHS } from '@/types';
import { HelpLink } from '@/components/HelpLink';

const defaultSchedule: ScheduleDefinition = {
  frequency: 'Monthly',
  timeOfDay: '08:00:00',
  timeZoneId: 'Europe/London',
  daysOfWeek: null,
  startDate: null,
  endDate: null,
  intervalDays: null,
  dayOfMonth: 1,
  monthInterval: null,
  months: ALL_MONTHS,
  cronExpression: null,
  humanReadable: null
};

const defaultParameters: KtInsightsParameters = {
  headOffice: null,
  dateRange: 'None',
  customFrom: null,
  customTo: null
};

/**
 * Relative ranges are the useful ones on a recurring schedule — the API resolves them
 * against the clock at dispatch, so "Previous month" always means the month that just
 * ended rather than a window frozen when the schedule was created.
 */
const dateRangeOptions: { value: KtInsightsDateRange; label: string }[] = [
  { value: 'None', label: 'All dates (no date filter)' },
  { value: 'PreviousMonth', label: 'Previous month' },
  { value: 'PreviousQuarter', label: 'Previous quarter' },
  { value: 'YearToDate', label: 'Year to date' },
  { value: 'Last7Days', label: 'Last 7 days' },
  { value: 'Last30Days', label: 'Last 30 days' },
  { value: 'Last90Days', label: 'Last 90 days' },
  { value: 'Custom', label: 'Fixed date range…' }
];

/**
 * KT Insights (Power BI service) surface: schedule curated report+page presets
 * for email delivery via the PBRS export queue, plus a read-only view of the
 * BI-team-managed PBRS deliveries.
 */
export function KtInsights() {
  return (
    <>
      <div className="page-header">
        <div>
          <h1>KT Insights</h1>
          <div className="sub">Schedule Power BI report exports — scoped to a head office and date range, delivered to you or a colleague through the KT Insights export pipeline. <HelpLink slug="schedule-a-kt-insights-export" /></div>
        </div>
      </div>

      <MyScheduledExports />
      <BiTeamDeliveries />
    </>
  );
}

// ── Portal-owned scheduled exports ─────────────────────────────

function MyScheduledExports() {
  const api = useApi();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  // The subscription being edited, held whole rather than by id: the form seeds itself from it, and
  // the list refetches every 10s — reading the row back out of `subs` on each render would let a
  // poll land mid-edit and reset the fields under the user.
  const [editing, setEditing] = useState<KtInsightsSubscription | null>(null);
  const [lastRun, setLastRun] = useState<KtInsightsRunNowResult | null>(null);
  // Which row has its explanation open, one at a time — the same `expanded === id` shape the Report
  // pickup page uses for its file details, so the two tables behave identically rather than each
  // inventing a way to expand.
  //
  // Rows whose strip is a FAILURE are deliberately not held here: {@link needsAttention} opens those
  // regardless, because a bad outcome nobody clicked on is a bad outcome nobody saw.
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: subs, isLoading } = useQuery({
    queryKey: ['ktinsights-subs'],
    queryFn: api.ktInsightsSubscriptions,
    refetchInterval: 10_000
  });

  // The same query key PickupToggle uses, so this shares its cache rather than adding a request.
  // Read here rather than inside the toggle because what needs saying about a KT Insights pickup
  // route — the folder it owns and where that folder came from — does not fit in a 132px column,
  // and because the column no longer holds a toggle at all: it reports which delivery methods this
  // export has, and the route is set up in the edit form where the rest of the setup lives.
  const { data: pickups } = useQuery({ queryKey: ['pickup-deliveries'], queryFn: api.pickupDeliveries });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ktinsights-subs'] });
    qc.invalidateQueries({ queryKey: ['outbox'] });
  };

  const del = useMutation({
    mutationFn: (id: string) => api.deleteKtInsightsSubscription(id),
    onSuccess: invalidate
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.enableKtInsightsSubscription(id, enabled),
    onSuccess: invalidate
  });
  const runNow = useMutation({
    mutationFn: (id: string) => api.runKtInsightsSubscription(id),
    onSuccess: result => { setLastRun(result); invalidate(); }
  });

  return (
    <div className="card" style={{ marginBottom: 22 }}>
      <div className="card-header">
        <h3>My scheduled exports</h3>
        <button
          className="btn-primary btn-sm"
          onClick={() => { setEditing(null); setCreating(c => !c); }}>
          {creating ? 'Close' : '+ Schedule an export'}
        </button>
      </div>
      <div className="card-body">
        {creating && (
          <ExportForm
            onSaved={() => { setCreating(false); invalidate(); }}
            onCancel={() => setCreating(false)}
          />
        )}

        {/* Keyed on the id so switching straight from editing one export to another rebuilds the
            form rather than leaving the first one's schedule and filters in place. */}
        {editing && (
          <ExportForm
            key={editing.id}
            existing={editing}
            onSaved={() => { setEditing(null); invalidate(); }}
            onCancel={() => setEditing(null)}
          />
        )}

        {lastRun && <RunNowNotice result={lastRun} onDismiss={() => setLastRun(null)} />}

        {isLoading ? (
          <div className="placeholder">Loading…</div>
        ) : (subs ?? []).length === 0 ? (
          !creating && (
            <div className="placeholder">
              No scheduled exports yet. Pick a report and the pages you want, and they'll arrive in
              your inbox on your schedule.
            </div>
          )
        ) : (
          <table className="data-table data-table--fixed data-table--compact">
            {/*
              Ten columns do not fit this card on their own terms, so the split is declared here
              rather than left to the browser to derive from whichever row happens to hold the
              longest value. Shares are in column order and total 100.

              Each one clears what its column cannot break, measured at the 1150px the table is
              floored to: a pill is nowrap by design (the widest, "No access"/"Rendering", wants
              131px with padding) and a date is held together the same way (104px). The rest is
              free text that wraps.

              Head office is a column rather than the third line of the Export cell because it is
              the field people scan DOWN — "which of these is Manchester's?" — and a value buried
              in an identity cell cannot be scanned. Its width came from Actions, which went from
              four wrapped buttons to one menu: actions are the one thing in a row nobody scans,
              because you use them after finding the row rather than to find it.

              "Delivers via" kept the width the Pickup toggle had rather than giving any of it back.
              It no longer holds a button, but it does hold a pill, and a pill is the one thing here
              that cannot be broken: at 10% — 115px at the floor, 83px inside the compact cell's
              16px padding — "Email + Pickup" as a single pill measures ~118px and would paint over
              the Actions trigger beside it, which is why {@link DeliversViaCell} stacks the two
              short lines instead of writing the phrase out on one.
            */}
            <colgroup>
              <col style={{ width: '15%' }} />{/* Export */}
              <col style={{ width: '12.5%' }} />{/* Head office */}
              <col style={{ width: '9%' }} />{/* Schedule */}
              <col style={{ width: '11%' }} />{/* Delivers to */}
              <col style={{ width: '8.5%' }} />{/* Next run */}
              <col style={{ width: '8.5%' }} />{/* Last run */}
              <col style={{ width: '10%' }} />{/* Dispatch */}
              <col style={{ width: '10%' }} />{/* Delivery */}
              <col style={{ width: '10%' }} />{/* Delivers via */}
              <col style={{ width: '5.5%' }} />{/* Actions — 63px at the floor; fits the 30px trigger */}
            </colgroup>
            <thead>
              <tr>
                <th>Export</th>
                <th>Head office</th>
                <th>Schedule</th>
                <th>Delivers to</th>
                <th>Next run</th>
                <th>Last run</th>
                <th>Dispatch</th>
                <th>Delivery</th>
                <th>Delivers via</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(subs ?? []).map(s => (
                <SubRow
                  key={s.id}
                  sub={s}
                  pickup={(pickups ?? []).find(p => p.sourceSubscriptionId === s.id && p.isEnabled)}
                  editing={editing?.id === s.id}
                  expanded={expanded === s.id}
                  onToggleExpand={() => setExpanded(e => e === s.id ? null : s.id)}
                  onEdit={() => { setCreating(false); setEditing(s); }}
                  onDelete={() => { if (confirm(`Delete '${s.presetTitle}'?`)) del.mutate(s.id); }}
                  onToggle={() => toggle.mutate({ id: s.id, enabled: !s.isEnabled })}
                  onRunNow={() => runNow.mutate(s.id)}
                  busy={del.isPending || toggle.isPending || runNow.isPending}
                />
              ))}
            </tbody>
          </table>
        )}

        {/* Under the table rather than over it: it explains one column, and a caveat placed above
            the exports themselves would out-shout the page's actual subject. */}
        {(subs ?? []).length > 0 && <KtInsightsPickupFolderNote />}
      </div>
    </div>
  );
}

/**
 * How the Pickup column picks a folder, stated once for the whole table.
 *
 * The column used to carry a longer notice explaining that nothing was ever written into these
 * folders. That is no longer true — an export with pickup on is queued as DeliveryMode 'Both' and
 * PBRS files a copy alongside the email — so what is left is the one thing about it that still
 * surprises people and has been asked about more than once: unlike a report server subscription,
 * the folder is not a choice made here. Somebody who leaves the head office blank (the default) and
 * expects a folder per client would find out by opening `_unfiled` and finding everybody's files
 * in it, which is exactly the kind of discovery this page has spent this release stopping.
 *
 * Asked as a question and closed by default, in the page's own quiet grey rather than the amber it
 * used to take. Amber is the colour this table gives a delivery that has gone wrong, and spending it
 * on a permanently-open paragraph that explains a working feature both cried wolf and, on a table of
 * two exports, put more warning-coloured prose on screen than data. The wording is unchanged: it is
 * still the answer, it just waits to be asked.
 */
function KtInsightsPickupFolderNote() {
  const api = useApi();
  const { data: status } = useQuery({ queryKey: ['pickup-status'], queryFn: api.pickupStatus });

  // Nothing to explain where no export can have a folder in the first place: pickup storage is not
  // configured in this environment, so there is no decision to describe.
  if (status && !status.isConfigured) return null;

  return (
    <details
      style={{
        marginTop: 14,
        border: '1px solid var(--kt-grey-200)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--kt-grey-50)',
        padding: '8px 12px',
        fontSize: '.82rem',
        color: 'var(--kt-grey-500)'
      }}
    >
      {/* The marker is the browser's own triangle. A hand-rolled chevron here would be a third
          expand affordance on one page — the rows have one, Report pickup has another — for no gain
          over the one every user already recognises. */}
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
        How does the pickup folder get chosen?
      </summary>
      <div style={{ marginTop: 8 }}>
        <strong>The pickup folder follows the export's head office — it is not one you choose here.</strong>
      </div>
      <div style={{ marginTop: 4 }}>
        This is the difference from a report server subscription, where you name the client the files
        are filed under. Set a head office on the export and its files are filed under that client;
        leave it blank — the default, meaning every customer the recipient can see — and there is no
        client to file under, so the export shares the single <code>_unfiled</code> folder with every
        other unfiled delivery.
      </div>
      <div style={{ marginTop: 6 }}>
        It is then fixed for the life of the route. Changing the head office later updates the client
        shown against it but does not move the folder, and switching pickup off and on again keeps
        the same one — moving it would strand every file already collected there. Nothing is deleted
        either way.
      </div>
    </details>
  );
}

/**
 * "Run now" used to return a bare 202 and leave the user with nothing to look at, which is
 * how a silently discarded export went unnoticed. Naming the queue id gives them something
 * concrete to quote, and points them at the Delivery column that resolves it.
 *
 * What that column can resolve is one-sided, and this now says so rather than inviting the user to
 * wait for a green pill that will not come: PBRS records nothing about an export the portal queued
 * beyond having accepted it, so the column can show the request being thrown away and cannot show
 * it arriving.
 */
function RunNowNotice({ result, onDismiss }: {
  result: KtInsightsRunNowResult;
  onDismiss: () => void;
}) {
  const failed = result.status === 'Failed';

  return (
    <div
      className={failed ? 'error-banner' : undefined}
      style={{
        marginBottom: 14, padding: '10px 14px', fontSize: '.85rem',
        display: 'flex', alignItems: 'flex-start', gap: 12,
        ...(failed ? {} : {
          background: 'var(--kt-grey-50)',
          border: '1px solid var(--kt-grey-200)',
          borderLeft: '3px solid var(--kt-green-mid)',
          borderRadius: 'var(--radius-sm)'
        })
      }}
    >
      <div style={{ flex: 1 }}>
        {failed ? (
          <>This export was not queued: {result.error}</>
        ) : result.queueId != null ? (
          <>
            Queued as <strong>export #{result.queueId}</strong>. The KT Insights pipeline renders and
            emails it — usually within a few minutes. Watch the Delivery column below: it turns red if
            the pipeline throws the request away. It will not turn green, because the pipeline records
            no outcome for exports the portal queues, so the inbox is what confirms the email arrived.
          </>
        ) : (
          <>
            Queued, but the pipeline did not return an export id, so this one cannot be followed
            through to delivery.
          </>
        )}
      </div>
      <button className="btn-ghost btn-sm" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

/**
 * The delivery outcome reuses the shared pill palette so a colour keeps its meaning across
 * the portal: green delivered, red gone, amber still in flight or stuck, grey not known.
 *
 * 'Accepted' is grey on purpose, and it is the colour this whole column turns on for most rows.
 * Green would claim the export arrived and the portal has no evidence of that — PBRS never writes
 * an outcome back for an export the portal queued. Amber would claim something is wrong, which is
 * what the old 'Waiting' pill did to exports the user had already received. Grey says what is
 * actually true: handed over, outcome unseen.
 */
const outcomeStyles: Record<KtInsightsExportOutcome, { label: string; cls: string }> = {
  Delivered: { label: 'Delivered', cls: 'pill-success' },
  Discarded: { label: 'Discarded', cls: 'pill-failed' },
  Failed: { label: 'Failed', cls: 'pill-failed' },
  NoAccess: { label: 'No access', cls: 'pill-failed' },
  Stalled: { label: 'Stuck', cls: 'pill-stale' },
  Rendering: { label: 'Rendering', cls: 'pill-pending' },
  Accepted: { label: 'Accepted', cls: 'pill-unknown' },
  Queued: { label: 'Queued', cls: 'pill-pending' },
  Unknown: { label: 'Unknown', cls: 'pill-unknown' }
};

function ExportOutcomePill({ status }: { status: KtInsightsExportStatus }) {
  const { label, cls } = outcomeStyles[status.outcome] ?? outcomeStyles.Unknown;
  return <span className={`pill ${cls}`} title={status.summary}>{label}</span>;
}

/**
 * A delivery that needs explaining rather than just labelling, or null when the pill says it all.
 *
 * 'Accepted' qualifies even though nothing has gone wrong: a one-word pill cannot say "the pipeline
 * has it and will never tell us what it did with it", and that sentence is the entire answer to
 * "I have the email, why does this not say Delivered?".
 */
function deliveryNote(sub: KtInsightsSubscription) {
  const status = sub.lastExport;
  if (!status) return null;
  return status.outcome === 'Discarded' || status.outcome === 'Stalled'
    || status.outcome === 'NoAccess' || status.outcome === 'Accepted'
    ? status
    : null;
}

/**
 * The delivery cell is the point of the whole page: "dispatch succeeded" only means the
 * request reached the queue, so a schedule whose export was thrown away has to look wrong
 * here rather than fine.
 *
 * The pill and the queue id are all that stay in the column — what a bad outcome means is said
 * underneath the row by {@link FailureStrip}, where there is width to say it in.
 */
function DeliveryCell({ sub }: { sub: KtInsightsSubscription }) {
  const status = sub.lastExport;

  if (!status) {
    return (
      <span style={{ color: 'var(--kt-grey-400)' }}>
        {sub.lastQueueId ? 'Checking…' : '—'}
      </span>
    );
  }

  return (
    <div>
      <ExportOutcomePill status={status} />
      <div style={{ fontSize: '.72rem', color: 'var(--kt-grey-400)', marginTop: 3 }}>
        export #{status.queueId}
      </div>
    </div>
  );
}

/**
 * What this schedule's last run and its pickup route need said about them, across the full width
 * of the table.
 *
 * Named for the row rather than for a column, because none of these belong to one: a dispatch that
 * never reached the queue, a delivery the pipeline will not describe, and the folder a copy is filed
 * in are three stages of the same schedule, and reading them together is how you tell "we never
 * queued it" from "we queued it and cannot see what happened next". Renders nothing at all for a row
 * with nothing to say, so it costs a quiet table no vertical space.
 *
 * Shown on demand rather than always, as of this release. Every line of it is multi-sentence prose,
 * and on a table of two exports the strips put roughly ten lines of grey paragraph under four lines
 * of data — the explanation had become the page. What decides whether a row opens itself is
 * {@link needsAttention} rather than merely having a strip: a benign explanation waits to be asked
 * for, a failure never does.
 */
function RowNotes({ sub, pickup }: { sub: KtInsightsSubscription; pickup?: PickupDeliveryDto }) {
  const delivery = deliveryNote(sub);
  if (!sub.lastError && !delivery && !pickup) return null;

  return (
    <tr className="run-detail-row">
      <td colSpan={10}>
        <div className="run-detail sub-failure">
          {sub.lastError && (
            <div className="sub-failure-item sub-failure-error">
              <strong>Dispatch</strong> — {sub.lastError}
            </div>
          )}
          {delivery && (
            <div
              className={deliveryToneClass(delivery.outcome)}
              style={delivery.outcome === 'Accepted' ? { color: 'var(--kt-grey-500)' } : undefined}>
              <strong>Delivery</strong> — {delivery.summary}
            </div>
          )}
          {pickup && <PickupNote pickup={pickup} />}
        </div>
      </td>
    </tr>
  );
}

/**
 * 'Accepted' takes no colour modifier, so it inherits the grey it is given inline and matches its
 * own pill. It is not a fault report — it is the portal saying what it does and does not know —
 * and putting it in the red or amber the other three use would be the old false alarm again in a
 * different typeface.
 */
function deliveryToneClass(outcome: KtInsightsExportOutcome) {
  if (outcome === 'Accepted') return 'sub-failure-item';
  return `sub-failure-item ${outcome === 'Stalled' ? 'sub-failure-stalled' : 'sub-failure-error'}`;
}

/**
 * Whether this row's strip is a report of something going WRONG rather than an explanation.
 *
 * The one exception to collapsing the strips, and the reason there is a predicate here at all: a
 * failure the user has to click to discover is worse than the noise collapsing them was meant to
 * remove. These rows open themselves and offer no way to shut them, because "I closed it" is not a
 * state the portal should be able to confuse with "nothing went wrong".
 *
 * `lastError` is dispatch — the request never reached the queue — and the four outcomes are the
 * pipeline's own bad answers. 'Accepted' is deliberately NOT here despite having a note: it is the
 * portal saying what it cannot see, which is exactly the benign prose this release is putting away.
 * 'Failed' has no {@link deliveryNote} of its own, so it will usually be paired with a dispatch
 * error; it is listed anyway so the rule reads as the set of bad outcomes rather than as whichever
 * of them happen to write a sentence.
 */
function needsAttention(sub: KtInsightsSubscription) {
  if (sub.lastError) return true;

  const outcome = sub.lastExport?.outcome;
  return outcome === 'Discarded' || outcome === 'Failed'
    || outcome === 'NoAccess' || outcome === 'Stalled';
}

/**
 * Whether {@link RowNotes} would render anything for this row.
 *
 * Asked BEFORE the strip is rendered, because the row needs to know whether to offer an expander:
 * a chevron on a row with nothing under it is a promise the table cannot keep. Kept in step with
 * RowNotes' own early return by testing the same three things.
 */
function hasRowNotes(sub: KtInsightsSubscription, pickup?: PickupDeliveryDto) {
  return Boolean(sub.lastError || deliveryNote(sub) || pickup);
}

/**
 * Which delivery methods this export is configured for — reported, not offered.
 *
 * This column used to hold `PickupToggle compact`, which put an "Add to pickup" / "Turn off" button
 * inside a table cell: setup living in the list, one narrow column wide, next to nine columns of
 * read-only reporting. Switching pickup on creates a second route on the estate, and that decision
 * belongs on the form with the head office it derives its folder from — see {@link ExportForm}.
 *
 * Not {@link describeDelivery}, deliberately, even though this is the same question the PBIRS list
 * answers with it. That helper reads a `SubscriptionPickupState` — a `health` the portal gets by
 * checking the report server's half of a PAIR — and a KT Insights export has no pair to check: what
 * this page holds is the portal's own `PickupDeliveryDto` delivery row. Forcing the shapes together
 * would mean inventing a health nobody verified, so the vocabulary is shared and the source is not:
 * "Email" and "Pickup" are the same words {@link describeDelivery} uses.
 *
 * Two lines rather than one "Email + Pickup" pill because of the column's width — see the colgroup
 * note above. Email is plain text and pickup is the pill, which is also the right emphasis: every
 * export emails, and the point of the column is the handful that do something else as well.
 */
function DeliversViaCell({ pickup }: { pickup?: PickupDeliveryDto }) {
  return (
    <div style={{ fontSize: '.85rem' }}>
      <span style={{ color: 'var(--kt-grey-500)' }}>Email</span>
      {pickup && (
        <div style={{ marginTop: 3 }}>
          <span className="pill pill-success" title={`Filed under ${pickup.clientFolder}`}>Pickup</span>
        </div>
      )}
    </div>
  );
}

/**
 * Where this export's files go, named in full for whoever switched pickup on.
 *
 * It reads as a statement rather than a warning, and takes the grey the 'Accepted' delivery note
 * takes for the same reason: this route works now — the export is queued as DeliveryMode 'Both' and
 * PBRS files a copy from the same package as the email — so amber here would be the false-alarm
 * problem again in a different place. What is worth saying is the destination itself, because the
 * client folder is derived rather than chosen and that is the part people query.
 */
function PickupNote({ pickup }: { pickup: PickupDeliveryDto }) {
  return (
    <div className="sub-failure-item" style={{ color: 'var(--kt-grey-500)' }}>
      <strong>Pickup</strong> — A copy of each export is filed in <code>{pickup.uncPath}</code>,
      under <strong>{pickup.clientFolder}</strong>, which comes from this export's head-office filter
      rather than from a folder chosen here. The email still goes out; pickup is an additional
      destination, not a replacement. Files appear on the Report pickup page once the portal's next
      indexing pass has seen them.
    </div>
  );
}

/**
 * A run time that may break between the date and the clock, but never inside either.
 *
 * The cell used to say `nowrap` and mean only this, but a cell cannot keep a rule to itself: the
 * failure message below inherited it too and rendered as one 1210px line straight across the
 * Delivery and Pickup columns — the overlap, and the sideways scrollbar with it. Marking the two
 * halves individually says the actual intent, and leaves everything else in the cell free to wrap.
 * Without it the cells' `overflow-wrap: anywhere` would happily split "04/08/2026," after the "20".
 */
function Timestamp({ utc }: { utc: string }) {
  const [date, time] = new Date(utc).toLocaleString().split(/,\s+/);
  return (
    <>
      <span style={{ whiteSpace: 'nowrap' }}>{date}{time ? ',' : ''}</span>
      {time && <> <span style={{ whiteSpace: 'nowrap' }}>{time}</span></>}
    </>
  );
}

function SubRow({ sub, pickup, editing, expanded, onToggleExpand, onEdit, onDelete, onToggle, onRunNow, busy }: {
  sub: KtInsightsSubscription;
  /** This export's pickup route, when it has one — see {@link PickupNote} for what that means here. */
  pickup?: PickupDeliveryDto;
  editing: boolean;
  /** Whether the user has asked for this row's explanation. Ignored on a row that opens itself. */
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  onRunNow: () => void;
  busy: boolean;
}) {
  // Two different reasons a strip is on screen, and only one of them is the user's decision.
  // A row that needs attention shows its strip whatever `expanded` says and offers no chevron:
  // there is nothing to toggle, because closing it is not something the portal should let anyone
  // do by accident. Everything else is an explanation, and waits to be asked for.
  const alarming = needsAttention(sub);
  const collapsible = hasRowNotes(sub, pickup) && !alarming;
  const open = alarming || (collapsible && expanded);

  return (
    <>
      <tr
        style={editing ? { background: 'var(--kt-teal-bg)' } : undefined}
        // The whole row, as on Report pickup — the chevron is the affordance, not the hit area, and
        // a 12px target would be the only thing on this page anyone had to aim at. RowActionsMenu
        // stops propagation on everything inside it, so the menu still opens without expanding.
        onClick={collapsible ? onToggleExpand : undefined}
        aria-expanded={collapsible ? expanded : undefined}
      >
        <td data-label="">
          <div className="report-name">
            {collapsible && (
              // A real button rather than a glyph on the row, so the explanation is reachable from
              // the keyboard at all; the row's own handler is a convenience on top of it.
              <button
                type="button"
                aria-expanded={expanded}
                aria-label={`${expanded ? 'Hide' : 'Show'} delivery details for ${sub.presetTitle}`}
                title={expanded ? 'Hide delivery details' : 'Show delivery details'}
                onClick={e => { e.stopPropagation(); onToggleExpand(); }}
                style={{
                  border: 0, background: 'none', padding: 0, marginRight: 6,
                  lineHeight: 1, fontSize: '.72rem', color: 'var(--kt-grey-400)'
                }}
              >
                <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
              </button>
            )}
            {sub.presetTitle}
          </div>
          {/* A count, not the names. Twenty-one page names is a paragraph, and it was setting the
              height of the whole row; the full list stays on the title attribute, where it was
              already, and the count is the part anyone reads. */}
          <div className="report-path" title={describePages(sub)}>
            {sub.reportName} · {describePageCount(sub)}
          </div>
        </td>
        <td data-label="Head office" style={{ fontSize: '.85rem' }}>
          {sub.parameters?.headOffice
            ? sub.parameters.headOffice
            : (
              // Not "—". A blank head office is not a missing value, it is the widest possible
              // scope, and on a row that goes to a person it is the thing most worth noticing.
              <span style={{ color: 'var(--kt-grey-400)' }}>All customers</span>
            )}
          {otherFilterCount(sub) > 0 && (
            <div className="chip-row">
              <span className="chip" title={sub.parametersDescription ?? undefined}>
                +{otherFilterCount(sub)} filter{otherFilterCount(sub) === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </td>
        <td data-label="Schedule" style={{ fontSize: '.85rem' }}>{sub.scheduleDescription}</td>
        <td data-label="Delivers to" style={{ fontSize: '.85rem' }}>
          {sub.recipientEmail}
          {sub.recipientDisplayName && (
            <div style={{ fontSize: '.75rem', color: 'var(--kt-grey-400)' }}>{sub.recipientDisplayName}</div>
          )}
        </td>
        <td data-label="Next run" style={{ fontSize: '.85rem' }}>
          {sub.isEnabled && sub.nextRunUtc ? <Timestamp utc={sub.nextRunUtc} /> : '—'}
        </td>
        <td data-label="Last run" style={{ fontSize: '.85rem' }}>
          {sub.lastRunUtc ? <Timestamp utc={sub.lastRunUtc} /> : '—'}
        </td>
        <td data-label="Dispatch"><StatusPill status={sub.isEnabled ? sub.lastStatus : 'Disabled'} /></td>
        <td data-label="Delivery"><DeliveryCell sub={sub} /></td>
        <td data-label="Delivers via">
          <DeliversViaCell pickup={pickup} />
          {/* Deliberately no caption under it, and no file count either. A list cell reports what
              this export is set up to do; how that route is behaving — where the files land, how
              many have arrived — is on the strip under the row, where there is width to say it in.
              Two half-answers in two places is how the old cell managed to be both cramped and
              redundant at once. */}
        </td>
        <td data-label="Actions">
          <RowActionsMenu
            busy={busy}
            ariaLabel={`Actions for ${sub.presetTitle}`}
            actions={[
              {
                label: 'Run now',
                onSelect: onRunNow,
                disabled: !sub.isEnabled,
                title: sub.isEnabled ? undefined : 'A disabled export cannot be run'
              },
              // Editable whether or not it is enabled: a disabled export is very often disabled
              // BECAUSE its schedule or recipient is wrong, and fixing it is the point.
              { label: 'Edit', onSelect: onEdit },
              { label: sub.isEnabled ? 'Disable' : 'Enable', onSelect: onToggle },
              { label: 'Delete', onSelect: onDelete, danger: true }
            ]}
          />
        </td>
      </tr>
      {open && <RowNotes sub={sub} pickup={pickup} />}
    </>
  );
}

/**
 * Schedules a new export, or edits an existing one.
 *
 * One component for both because everything below the preset is the same question asked the same
 * way — what it covers, who gets it, when it arrives — and the edit form drifting away from the
 * create form is how the two ends up validating differently from each other. The API takes the
 * same view: `UpdateKtInsightsSubscriptionRequest` is the create request minus the preset, and the
 * service runs the identical parameter and recipient checks over it.
 *
 * The preset is the one thing an edit cannot change, and it is left off rather than disabled: a
 * different report and pages is a different export, not an adjustment to this one, and the delivery
 * history hanging off the row would stop describing anything anybody scheduled.
 */
function ExportForm({ existing, onSaved, onCancel }: {
  existing?: KtInsightsSubscription;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const api = useApi();
  const editing = existing !== undefined;

  // Seeded from the subscription when editing. An edit states the WHOLE desired state — the API
  // replaces rather than merges — so the form has to start out holding everything the row already
  // has, or saving would quietly clear whatever was not re-entered.
  const [reportId, setReportId] = useState<number | null>(existing?.reportId ?? null);
  // Seeded from the stored CSV, which is what the row holds and what the API takes back.
  const [pages, setPages] = useState<number[]>(parsePages(existing?.pageOrderIdsCsv));
  const [schedule, setSchedule] = useState<ScheduleDefinition>(existing?.schedule ?? defaultSchedule);
  const [parameters, setParameters] = useState<KtInsightsParameters>(existing?.parameters ?? defaultParameters);
  const [recipientEmail, setRecipientEmail] = useState(existing?.recipientEmail ?? '');

  // Blank means "send it to me", so the entitlement being checked is the signed-in user's own.
  // Resolving that here rather than waiting for someone to type their own address is what makes
  // the picker's page marks appear on the common path at all.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: Infinity });
  const recipient = useRecipientCheck(reportId, recipientEmail, me?.email);

  // Returns nothing on either branch: create answers with the new id and update with 204, and the
  // caller's only response to both is to close the form and refetch the list. Discarding it here
  // keeps the two arms one mutation rather than a union nobody reads.
  const save = useMutation<void, Error>({
    mutationFn: async () => {
      if (editing) {
        await api.updateKtInsightsSubscription(existing.id, {
          schedule,
          parameters,
          recipientEmail: recipientEmail.trim() || null
        });
        return;
      }

      await api.createKtInsightsSubscription({
        reportId: reportId!,
        // Sorted, so the CSV is the same for the same set of pages however they were ticked. The
        // API re-orders it into the report's own page order anyway; this just keeps what is sent
        // stable enough to compare.
        pageOrderIdsCsv: [...pages].sort((a, b) => a - b).join(','),
        schedule,
        parameters,
        recipientEmail: recipientEmail.trim() || null
      });
    },
    onSuccess: onSaved
  });

  // Numbered for the create form, where they are steps through a decision. Editing is not a
  // wizard — the user came to change one of these — so the numbers would be describing an order
  // nobody is following.
  const step = (n: number, label: string) => editing ? label : `${n}. ${label}`;

  return (
    <div style={{
      border: '1px solid var(--kt-grey-200)', borderRadius: 'var(--radius-sm)',
      padding: 18, marginBottom: 18, background: 'var(--kt-grey-50)'
    }}>
      {editing ? (
        <div className="field">
          <label>Editing</label>
          <div style={{ fontWeight: 700, color: 'var(--kt-green-dark)', fontSize: '.92rem' }}>
            {existing.presetTitle}
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginTop: 3 }}>
            {existing.reportName} · {describePages(existing)} — to send a different report or
            different pages, schedule a new export and delete this one.
          </div>
        </div>
      ) : (
        <div className="field">
          <label>1. Choose what to send</label>
          <KtInsightsPagePicker
            reportId={reportId}
            selected={pages}
            onChange={(report, next) => { setReportId(report); setPages(next); }}
            entitlement={entitlementOf(recipient.data, recipient.label)}
          />
        </div>
      )}

      <div className="field" style={{ marginTop: 16 }}>
        <label>{step(2, 'What should it cover?')}</label>
        <ParametersEditor reportId={reportId} value={parameters} onChange={setParameters} />
      </div>

      <div className="field" style={{ marginTop: 16 }}>
        <label>{step(3, 'Who receives it?')}</label>
        <RecipientPicker
          value={recipientEmail}
          onChange={setRecipientEmail}
          recipient={recipient.data}
          isFetching={recipient.isFetching}
        />
        {/*
          Said here rather than offered as a fourth step, because the create API cannot carry it:
          `CreateKtInsightsSubscriptionRequest` has a report, pages, a schedule, parameters, a
          recipient and a title, and no pickup field of any kind. (The PBIRS create form does have
          one — `CreateSubscriptionRequest.pickupDelivery` — which is what PickupDestinationChoice
          exists for, and why the absence here is worth stating rather than assuming.) A checkbox
          the request could not carry would silently create an email-only export and tell the user
          otherwise, so the form says when the choice becomes available instead.
        */}
        {!editing && (
          <div className="field-hint" style={{ marginTop: 10 }}>
            Every export is emailed. To also file each run into report pickup, schedule the export
            and then edit it — the option appears once it exists, because the pickup route is
            attached to the saved export.
          </div>
        )}
      </div>

      {/*
        Pickup setup, on the form with everything else that is set up rather than in a table cell.
        Edit only: switching it on creates a route against THIS subscription's id, which is the one
        thing a create does not have yet.

        PickupToggle seeds its client field once, from the subscription as saved rather than from
        the head office in the form above — an unsaved edit is not what the route would be built
        against, and prefilling from it would put a client name on a folder for a filter nobody has
        committed to yet.
      */}
      {editing && (
        <div className="field" style={{ marginTop: 16 }}>
          <label>Should it also go to report pickup?</label>
          <PickupToggle
            key={existing.id}
            subscriptionId={existing.id}
            producer="KtInsights"
            suggestedClientName={existing.parameters?.headOffice}
          />
          <div className="field-hint" style={{ marginTop: 6 }}>
            The folder the files are filed under comes from this export's head office — see “How does
            the pickup folder get chosen?” under the table.
          </div>
        </div>
      )}

      <div className="field" style={{ marginTop: 16 }}>
        <label>{step(4, 'When should it arrive?')}</label>
        <ScheduleEditor value={schedule} onChange={setSchedule} />
        {editing && !existing.isEnabled && (
          // Saving deliberately does not re-enable it — that is a separate decision, and silently
          // restarting deliveries to a customer because someone corrected a filter would be the
          // portal doing something nobody asked for.
          <div style={{ marginTop: 8, fontSize: '.8rem', color: 'var(--kt-grey-500)' }}>
            This export is disabled, so it will not run on this schedule until you enable it.
          </div>
        )}
      </div>

      {save.error && (
        <div className="error-banner" style={{ marginTop: 12 }}>
          {(save.error as Error).message.replace(/^API \d+: /, '')}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn-secondary" onClick={onCancel}>Cancel</button>
        <button
          className="btn-primary"
          // A report with no pages ticked would queue DefaultPageOrderIds = NULL, which PBRS reads
          // as "every page this recipient can see" — a different export from the one on screen.
          disabled={(!editing && (!reportId || pages.length === 0)) || save.isPending}
          onClick={() => save.mutate()}>
          {save.isPending
            ? (editing ? 'Saving…' : 'Scheduling…')
            : (editing ? 'Save changes' : 'Schedule export')}
        </button>
      </div>
    </div>
  );
}

function ParametersEditor({ reportId, value, onChange }: {
  reportId: number | null;
  value: KtInsightsParameters;
  onChange: (p: KtInsightsParameters) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <HeadOfficePicker
        value={value.headOffice ?? null}
        onChange={headOffice => onChange({ ...value, headOffice })}
      />

      <KtInsightsFiltersEditor reportId={reportId} value={value} onChange={onChange} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 240 }}>
          <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginBottom: 4 }}>Date range</div>
          <select
            value={value.dateRange}
            onChange={e => onChange({ ...value, dateRange: e.target.value as KtInsightsDateRange })}
            style={{ width: '100%' }}
          >
            {dateRangeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {value.dateRange === 'Custom' && (
          <>
            <div>
              <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginBottom: 4 }}>From</div>
              <input
                type="date"
                value={value.customFrom ?? ''}
                onChange={e => onChange({ ...value, customFrom: e.target.value || null })}
              />
            </div>
            <div>
              <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginBottom: 4 }}>To</div>
              <input
                type="date"
                value={value.customTo ?? ''}
                onChange={e => onChange({ ...value, customTo: e.target.value || null })}
              />
            </div>
          </>
        )}
      </div>

      {value.dateRange !== 'None' && value.dateRange !== 'Custom' && (
        <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)' }}>
          Worked out each time the export runs, so a recurring schedule always covers the latest period.
        </div>
      )}
    </div>
  );
}

/** ~13,000 head offices live in the Power BI customer dimension, so this searches rather than lists. */
function HeadOfficePicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const api = useApi();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data: matches, isFetching } = useQuery({
    queryKey: ['ktinsights-head-offices', debounced],
    queryFn: () => api.ktInsightsHeadOffices(debounced),
    enabled: debounced.length >= 2 && !value
  });

  if (value) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div>
          <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)' }}>Head office</div>
          <strong style={{ fontSize: '.9rem' }}>{value}</strong>
        </div>
        <button className="btn-ghost btn-sm" onClick={() => { onChange(null); setTerm(''); }}>Change</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginBottom: 4 }}>
        Head office <span style={{ color: 'var(--kt-grey-400)' }}>— leave blank for every customer the recipient can see</span>
      </div>
      <input
        type="search"
        placeholder="Search head offices…"
        value={term}
        onChange={e => setTerm(e.target.value)}
        style={{ width: '100%', maxWidth: 420 }}
      />
      {debounced.length >= 2 && (
        <div style={{
          marginTop: 6, maxWidth: 420, maxHeight: 190, overflowY: 'auto',
          // Tokenised, not literal: a hard-coded #fff kept this panel white in dark mode, which put
          // the pale option labels at 1.15:1 — a result list you cannot read at all, on the one
          // control whose entire job is being read.
          border: '1px solid var(--kt-grey-200)', borderRadius: 'var(--radius-sm)', background: 'var(--kt-white)'
        }}>
          {isFetching && <div className="placeholder" style={{ padding: 8 }}>Searching…</div>}
          {!isFetching && (matches ?? []).length === 0 && (
            <div className="placeholder" style={{ padding: 8 }}>No head office matches “{debounced}”.</div>
          )}
          {(matches ?? []).map(h => (
            <div
              key={`${h.headOfficeId ?? h.name}`}
              onClick={() => onChange(h.name)}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '.85rem', borderBottom: '1px solid var(--kt-grey-100)' }}
            >
              {h.name}
              {h.region && <span style={{ color: 'var(--kt-grey-400)', marginLeft: 6 }}>{h.region}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What KT Insights knows about whoever will receive this export, resolved once for the form.
 *
 * Lifted out of the recipient field because two steps need the answer: step 3 says who the person
 * is, and step 1 marks the pages they cannot receive. Two components asking separately would be
 * two round trips to MANDW01 for one fact.
 *
 * The query is keyed on the report and the address and NOT on the pages, which is what keeps a
 * 27-page picker usable: with pages in the key every tick would refetch. The answer without pages
 * is the recipient's whole entitlement for the report, and the intersection with what is ticked is
 * arithmetic the browser can do.
 */
function useRecipientCheck(reportId: number | null, typed: string, ownEmail: string | undefined) {
  const api = useApi();
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(typed.trim()), 400);
    return () => clearTimeout(t);
  }, [typed]);

  // Blank means "send it to me" on both create and edit, so the address checked is the signed-in
  // user's own — the same fallback the API applies when it saves.
  const email = debounced || ownEmail || '';

  const query = useQuery({
    queryKey: ['ktinsights-recipient', reportId, email],
    queryFn: () => api.ktInsightsRecipientCheck(reportId!, email),
    enabled: reportId != null && email.includes('@') && email.length > 3,
    retry: false
  });

  return {
    ...query,
    /**
     * What to call the recipient in the picker's page marks: their resolved name, the address as
     * typed, or "you" for the blank-means-me default. Never a guess — and while a newly typed
     * address is still resolving there is no data, so the marks disappear rather than describing
     * the previous recipient.
     */
    label: debounced ? (query.data?.displayName ?? debounced) : 'you'
  };
}

/**
 * The recipient's page entitlement, or undefined when there is nothing trustworthy to mark with.
 *
 * Undefined for both "not resolved yet" and "the estate could not be read", and that is the point:
 * marking pages as unavailable on the strength of a failed lookup would be the portal telling an
 * account manager their colleague has lost access when nothing of the sort is known.
 */
function entitlementOf(recipient: KtInsightsRecipient | undefined, label: string): PageEntitlement | undefined {
  if (!recipient || recipient.status !== 'Resolved') return undefined;
  return {
    deliverable: new Set(parsePages(recipient.deliverablePageOrderIdsCsv)),
    recipientLabel: label
  };
}

/** The stored CSV as page ids. Tolerates the blank and the malformed; both mean "nothing ticked". */
function parsePages(csv: string | null | undefined): number[] {
  return (csv ?? '')
    .split(',')
    .map(p => Number(p.trim()))
    .filter(p => Number.isInteger(p) && p > 0);
}

/**
 * How a row says what it sends.
 *
 * Names when the row has one per page, and the count check is not defensive padding — the old
 * "Full report" preset carried 22 page ids and 17 labels, so a row created from it cannot line its
 * names up against its pages and saying "Travel Spend by Date, …" would be putting names to the
 * wrong pages. Those rows fall back to the ids, which is at least true.
 */
/**
 * "21 pages", or the one page's name when there is only one.
 *
 * A count answers what the list was being used for — is this the whole report or a slice — in one
 * line instead of two wrapped ones. A single page keeps its name, because "1 page" tells you
 * nothing that "Travel Spend by Booker" doesn't tell you better.
 */
function describePageCount(sub: KtInsightsSubscription) {
  const ids = parsePages(sub.pageOrderIdsCsv);
  const labels = sub.pageLabels ?? [];

  if (ids.length === 1) return labels.length === 1 ? labels[0] : '1 page';
  return `${ids.length} pages`;
}

/**
 * How many filters are set BESIDES the head office, which has its own column.
 *
 * Counted from the structured `parameters`, not from `parametersDescription` — that string is
 * stored, and is also what the audit trail and the delivered email quote, so it is not something to
 * re-parse for a count.
 */
function otherFilterCount(sub: KtInsightsSubscription) {
  const active = (sub.parameters?.filters ?? []).filter(f => f.values.length > 0).length;
  const dated = (sub.parameters?.dateRange ?? 'None') !== 'None' ? 1 : 0;
  return active + dated;
}

function describePages(sub: KtInsightsSubscription) {
  const ids = parsePages(sub.pageOrderIdsCsv);
  const labels = sub.pageLabels ?? [];

  if (labels.length > 0 && labels.length === ids.length) return labels.join(', ');
  if (labels.length > 0) return `${ids.length} pages (${sub.pageOrderIdsCsv})`;
  return `pages ${sub.pageOrderIdsCsv}`;
}

/**
 * Checks the address against KT Insights before the schedule is saved. The PBRS pipeline
 * refuses to deliver to anyone without report access anyway — this just means the user
 * hears about it now rather than wondering why no email ever arrives.
 *
 * The lookup itself belongs to the form ({@link useRecipientCheck}) rather than to this field,
 * because the page picker in step 1 needs the same answer and one MANDW01 round trip should serve
 * both. What is left here is the display.
 */
function RecipientPicker({ value, onChange, recipient, isFetching }: {
  value: string;
  onChange: (v: string) => void;
  recipient: KtInsightsRecipient | undefined;
  isFetching: boolean;
}) {
  return (
    <div>
      <input
        type="email"
        placeholder="Leave blank to send to yourself"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', maxWidth: 420 }}
      />

      {!value.trim() && (
        <div style={{ marginTop: 6, fontSize: '.8rem', color: 'var(--kt-grey-500)' }}>
          Delivered to your own email address.
        </div>
      )}

      {isFetching && <div style={{ marginTop: 6, fontSize: '.8rem', color: 'var(--kt-grey-500)' }}>Checking…</div>}

      {recipient && !isFetching && (
        <div style={{ marginTop: 8, fontSize: '.82rem' }}>
          {recipient.status === 'Resolved' && (
            <div style={{ color: 'var(--kt-grey-600, var(--kt-grey-500))' }}>
              <strong>{recipient.displayName ?? recipient.email}</strong>
              {recipient.roleName && <span style={{ color: 'var(--kt-grey-400)' }}> · {recipient.roleName}</span>}
            </div>
          )}
          {recipient.status === 'NotAUser' && (
            <div style={{ color: 'var(--kt-red)' }}>
              Not a KT Insights user — this export could not be delivered.
            </div>
          )}
          {/*
            An outage is amber, not red, and says so in the first person. Red here used to
            read as a verdict on the recipient; it is a verdict on our own connection, and
            the export can still be scheduled.
          */}
          {recipient.status === 'Unavailable' && (
            <div style={{ color: 'var(--kt-amber, #a06800)' }}>
              Could not check this recipient's KT Insights access right now — you can still schedule the export.
            </div>
          )}
          {recipient.warnings.map((w, i) => (
            <div key={i} style={{ marginTop: 4, color: recipient.status === 'NotAUser' ? 'var(--kt-red)' : 'var(--kt-amber, #a06800)' }}>
              {w}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Read-only BI-team estate ───────────────────────────────────

function BiTeamDeliveries() {
  const api = useApi();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ktinsights'],
    queryFn: api.ktInsightsDeliveries,
    refetchInterval: 60_000
  });

  if (isLoading) return <div className="placeholder">Loading KT Insights deliveries…</div>;
  if (error) return <div className="error-banner">Could not load: {(error as Error).message}</div>;

  const deliveries = data?.deliveries ?? [];

  return (
    <div className="card">
      <div className="card-header">
        <h3>BI-team scheduled deliveries</h3>
        <span style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)' }}>
          {data?.source === 'live' && 'Live from the report server · view only'}
          {data?.source === 'sample' && 'Sample data — report server not reachable'}
          {data?.source === 'unconfigured' && 'Not configured'}
        </span>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Export</th>
            <th>Trigger</th>
            <th>Delivers to</th>
            <th>Format</th>
            <th>Last delivery</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {deliveries.length === 0 ? (
            <tr><td colSpan={6} className="placeholder">No KT Insights scheduled deliveries found.</td></tr>
          ) : deliveries.map(d => <DeliveryRow key={d.scheduleId} d={d} />)}
        </tbody>
      </table>
    </div>
  );
}

function DeliveryRow({ d }: { d: KtInsightsDelivery }) {
  const run = d.lastRun;
  const failedCheck = run?.datasetCheckStatus && run.datasetCheckStatus.toLowerCase() !== 'refreshed'
    && run.outcome === 'Failed';

  return (
    <tr>
      <td data-label="">
        <div className="report-name">{d.exportName}</div>
        <div className="report-path">
          {d.exportType}{d.ownerUser ? ` · owner ${d.ownerUser}` : ''}{d.live ? '' : ' · NOT LIVE'}
        </div>
      </td>
      <td data-label="Trigger" style={{ minWidth: 160, maxWidth: 220, fontSize: '.85rem' }}>{d.triggerDescription}</td>
      <td data-label="Delivers to">
        {d.recipients.length > 0 ? (
          <>
            {d.recipients.slice(0, 2).join(', ')}
            {d.recipients.length > 2 && <span style={{ color: 'var(--kt-grey-400)' }}> +{d.recipients.length - 2} more</span>}
          </>
        ) : d.destinationSummary ? (
          <span style={{ fontSize: '.82rem', fontFamily: 'ui-monospace, Consolas, monospace', color: 'var(--kt-grey-500)' }}>
            {d.destinationSummary}
          </span>
        ) : (
          <span style={{ color: 'var(--kt-grey-400)' }}>—</span>
        )}
      </td>
      <td data-label="Format">{d.outputFormat ?? '—'}</td>
      <td data-label="Last delivery" style={{ fontSize: '.85rem' }}>
        {run?.sentUtc ? new Date(run.sentUtc).toLocaleString() : '—'}
        {failedCheck && (
          <div style={{ color: 'var(--kt-red)', fontSize: '.78rem', marginTop: 2 }}>
            dataset check: {run!.datasetCheckStatus}
          </div>
        )}
      </td>
      <td data-label="Status"><StatusPill status={run?.outcome ?? 'Unknown'} /></td>
    </tr>
  );
}
